/**
 * Analytics Slice
 *
 * Manages analytics state including initialization, identification, and consent.
 * Used with RTK Listener Middleware to capture Redux action events.
 *
 * Story 4.1: PostHog Frontend Analytics
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Analytics State Interface
 */
export interface AnalyticsState {
  /** Whether analytics SDK is initialized */
  initialized: boolean;
  /** Whether user has been identified */
  identified: boolean;
  /** Whether user has given consent for tracking */
  consentGiven: boolean;
  /** User ID for tracking (custom:userId from Cognito) */
  userId: string | null;
}

/**
 * Initial State
 */
const initialState: AnalyticsState = {
  initialized: false,
  identified: false,
  consentGiven: false,
  userId: null,
};

/**
 * Analytics Slice
 */
const analyticsSlice = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    /**
     * Initialize analytics SDK
     * Sets the initialized flag to true
     */
    initialize: (state) => {
      state.initialized = true;
    },

    /**
     * Identify user for tracking
     *
     * @param action.payload.userId - User ID (custom:userId from Cognito)
     * @param action.payload.email - Optional user email
     */
    identify: (
      state,
      action: PayloadAction<{ userId: string; email?: string }>
    ) => {
      state.identified = true;
      state.userId = action.payload.userId;
    },

    /**
     * Set consent status
     *
     * @param action.payload - true if user consents, false otherwise
     */
    setConsent: (state, action: PayloadAction<boolean>) => {
      state.consentGiven = action.payload;
    },

    /**
     * Reset analytics state (on logout)
     *
     * Clears identification but preserves consentGiven across sessions.
     */
    resetAnalytics: (state) => {
      state.identified = false;
      state.userId = null;
      // Note: consentGiven persists across sessions
    },

    // ============================================
    // View Actions - Trigger Middleware Event Listeners
    // ============================================

    /**
     * Triggered when user views settings page
     * Middleware listens to capture settings_viewed event
     */
    viewSettings: () => {
      // Intentionally empty - middleware listens to this action
    },

    /**
     * Triggered when user views login page
     * Middleware listens to capture page_viewed event
     */
    viewLogin: () => {
      // Intentionally empty - middleware listens to this action
    },

    /**
     * Triggered when user views profile page
     * Middleware listens to capture profile_viewed event
     *
     * @param action.payload.profileId - Optional profile ID being viewed
     * @param action.payload.isOwner - Whether viewing own profile
     */
    viewProfile: (
      _state,
      _action: PayloadAction<{ profileId?: string; isOwner?: boolean }>
    ) => {
      // Intentionally empty - middleware listens to this action
    },

    /**
     * Triggered when user views Trophy Room
     * Middleware listens to capture trophy_room_viewed event
     *
     * @param action.payload.ownerId - Optional owner ID
     * @param action.payload.isOwner - Whether viewing own trophy room
     * @param action.payload.itemCount - Number of items in trophy room
     */
    viewTrophyRoom: (
      _state,
      _action: PayloadAction<{
        ownerId?: string;
        isOwner: boolean;
        itemCount?: number;
      }>
    ) => {
      // Intentionally empty - middleware listens to this action
    },
  },
});

/**
 * Actions
 */
export const {
  initialize,
  identify,
  setConsent,
  resetAnalytics,
  viewSettings,
  viewLogin,
  viewProfile,
  viewTrophyRoom,
} = analyticsSlice.actions;

/**
 * Selectors
 */
export const selectIsAnalyticsInitialized = (state: {
  analytics: AnalyticsState;
}) => state.analytics.initialized;

export const selectIsIdentified = (state: { analytics: AnalyticsState }) =>
  state.analytics.identified;

export const selectConsentGiven = (state: { analytics: AnalyticsState }) =>
  state.analytics.consentGiven;

export const selectAnalyticsUserId = (state: { analytics: AnalyticsState }) =>
  state.analytics.userId;

/**
 * Reducer
 */
export default analyticsSlice.reducer;
